import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// Container for the collapsible section
const CollapsibleContainer = styled.div`
  margin: 20px 20px;
  background-color: ${({ theme }) => (theme === 'dark' ? '#34495e' : '#ffffff')};
  background: linear-gradient(180deg, #1a181c, #533d66);
  border-radius: 15px;
  box-shadow: ${({ theme }) =>
    theme === 'dark' ? '0 8px 20px rgba(0, 0, 0, 0.4)' : '0 4px 10px rgba(0, 0, 0, 0.1)'};
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    margin: 10px 5px; /* Adjust margin for mobile */
  }
`;

// The header itself will become the loading bar.
const CollapsibleHeader = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'progress', // Prevent `progress` from being forwarded to the DOM
})`
  padding: 16px;
  color: ${({ theme }) => (theme === 'dark' ? '#ecf0f1' : '#2c3e50')};
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  border-radius: 12px;
  transition: background-color 0.3s ease;
  
  /* Apply gradient and width transition */
  background: ${({ progress, theme }) =>
    progress > 0 ? `linear-gradient(90deg, #a284ff ${progress}%, rgba(224, 235, 255, 1) ${progress}%)` :
      theme === 'dark' ? '#2c3e50' : '#f9fafb'}; 

  /* Transition effect for smooth fill */
  transition: background 0.4s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => (theme === 'dark' ? '#34495e' : '#e4e9f2')};
  }

  /* Ensure text doesn't overflow */
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: clamp(1rem, 4vw, 1.2rem); /* Dynamically scale font size */
  }
`;

const CollapsibleHeaderText = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// Positioned text in the center with premium typography (for progress)
const ProgressText = styled.div`
  color: #7f518a;
  font-weight: 600;
  font-size: 14px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  @media screen and (max-width: 768px) {
    font-size: 12px; /* Adjust font size on mobile */
  }
`;

// Content inside the collapsible section (the table)
const CollapsibleContent = styled.div`
  padding: 16px;
  color: ${({ theme }) => (theme === 'dark' ? '#ecf0f1' : '#2c3e50')};
  font-size: 1rem;
  background-color: ${({ theme }) => (theme === 'dark' ? '#34495e' : '#fafafa')};
  background: linear-gradient(180deg, #1a181c, #533d66);
  border-radius: 0 0 12px 12px;
  box-shadow: ${({ theme }) =>
    theme === 'dark' ? '0 4px 10px rgba(0, 0, 0, 0.3)' : '0 4px 10px rgba(0, 0, 0, 0.1)'};
  
  @media (max-width: 768px) {
    padding: 10px; /* Adjust padding for mobile */
  }
`;

// Table container for displaying details inside the collapsible content
const TableContainer = styled.div`
  width: 100%;
  margin: 30px auto;
  background-color: ${({ theme }) => (theme === 'dark' ? '#2c3e50' : '#ffffff')};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme === 'dark' ? '0 10px 30px rgba(0, 0, 0, 0.3)' : '0 6px 15px rgba(0, 0, 0, 0.1)'};
  padding: 24px;
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: 16px; /* Adjust padding for mobile */
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableCell = styled.td`
  padding: 16px 20px;
  font-size: 1.2rem;
  color: ${({ theme }) => (theme === 'dark' ? '#ecf0f1' : '#2c3e50')};
  font-weight: 500;
  text-align: left;
  border-bottom: 1px solid ${({ theme }) => (theme === 'dark' ? '#34495e' : '#f1f1f1')};
  transition: background-color 0.3s ease;
  letter-spacing: 0.5px;

  &:nth-child(odd) {
    font-weight: 600;
  }

  &:hover {
    background-color: ${({ theme }) => (theme === 'dark' ? '#34495e' : '#fafafa')};
    cursor: pointer;
  }
`;

const TableRow = styled.tr`
  background-color: ${({ theme, $isEven }) =>
    theme === 'dark' ? ($isEven ? '#34495e' : '#2c3e50') : '#ffffff'};
`;

// The icon for the plus/minus signs
const ToggleIcon = styled.span`
  font-size: 20px;
  font-weight: bold;
  margin-left: 8px;
  color: #7f518a;
`;

// Main component to combine everything
const AnimatedCollapsibleLoadingBar = ({ title, data, theme }) => {
  const [progress, setProgress] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Initialize progress from the data
    setProgress(data.progress);

    // If progress is already 100, open the collapsible immediately
    if (data.progress === 100) {
      setIsOpen(true);
    }
  }, [data.progress]);

  // Extract data properties
  const { scanId, status, url } = data;

  // Prepare data for rendering in the table
  const tableData = [
    { label: 'Scan ID', value: scanId },
    { label: 'Status', value: status },
    { label: 'URL', value: url },
    { label: 'Progress', value: `${progress}%` }, // Display the dynamic progress value
  ];

  return (
    <CollapsibleContainer theme={theme}>
      <CollapsibleHeader theme={theme} progress={progress} onClick={() => setIsOpen(!isOpen)}>
        <CollapsibleHeaderText>
          {/* Display title */}
          <span>{`Scan Report: ${url}`}</span>

          {/* Loading progress in the header */}
          <ProgressText>{progress}%</ProgressText>

          {/* Plus/Minus sign */}
          <ToggleIcon>{isOpen ? '－' : '＋'}</ToggleIcon>
        </CollapsibleHeaderText>
      </CollapsibleHeader>

      {/* Show content once the loading is complete */}
      {isOpen && (
        <CollapsibleContent theme={theme}>
          <TableContainer theme={theme}>
            <Table>
              <tbody>
                {tableData.map((item, index) => (
                  <TableRow key={index} theme={theme} $isEven={index % 2 === 0}>
                    <TableCell theme={theme}>{item.label}</TableCell>
                    <TableCell theme={theme}>{item.value}</TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </CollapsibleContent>
      )}
    </CollapsibleContainer>
  );
};

export default AnimatedCollapsibleLoadingBar;
